import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function CheckoutPage() {
    const { token, user, checkAuthStatus } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isPaymentGatewayLoaded, setIsPaymentGatewayLoaded] = useState(false);
    const [pricingPlans, setPricingPlans] = useState([]);

    useEffect(() => {
        // Load Cashfree Script dynamically
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.onload = () => setIsPaymentGatewayLoaded(true);
        script.onerror = () => setMessage('Failed to load payment gateway. Please check your internet connection.');
        document.body.appendChild(script);

        // Fetch dynamic pricing plans
        fetch('https://healthcard.searchisend.com/health_api/get_pricing_plans.php')
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setPricingPlans(data);
                }
            })
            .catch(err => console.error("Error fetching pricing plans:", err));

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const handlePayment = async (plan) => {
        if (!isPaymentGatewayLoaded) {
            setMessage('Payment gateway is still loading. Please wait...');
            return;
        }

        if (!window.Cashfree) {
            setMessage('Failed to initialize Cashfree SDK.');
            return;
        }

        setIsLoading(true);
        setMessage('Initializing secure payment...');

        try {
            // 1. Create Order on Backend
            const orderResponse = await fetch('https://healthcard.searchisend.com/health_api/create_subscription.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ pricing_plan_id: plan.id })
            });

            const orderData = await orderResponse.json();
            if (!orderResponse.ok) throw new Error(orderData.message);

            // 2. Open Cashfree Checkout Modal
            setMessage('Opening secure checkout...');
            
            // NOTE: Change to 'production' for live environment
            const cashfree = window.Cashfree({ mode: 'production' });
            
            let checkoutOptions = {
                paymentSessionId: orderData.payment_session_id,
                redirectTarget: "_modal"
            };

            cashfree.checkout(checkoutOptions).then(async (result) => {
                if(result.error){
                    setMessage(`Payment failed: ${result.error.message}`);
                    setIsLoading(false);
                    return;
                }
                if(result.redirect){
                    setMessage("Redirecting...");
                    return;
                }
                if(result.paymentDetails){
                    setMessage('Payment captured! Verifying securely...');
                    
                    // 3. Verify Payment on Backend
                    try {
                        const verifyResponse = await fetch('https://healthcard.searchisend.com/health_api/verify_payment.php', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                order_id: orderData.id,
                                pricing_plan_id: plan.id
                            })
                        });

                        const verifyData = await verifyResponse.json();
                        if (!verifyResponse.ok) throw new Error(verifyData.message);

                        setMessage('Payment Successful! Your Health Card is active.');

                        // Refresh User Context and go to dashboard
                        await checkAuthStatus();
                        setTimeout(() => {
                            navigate('/my-dashboard');
                        }, 2000);

                    } catch (verifyError) {
                        setMessage(`Verification failed: ${verifyError.message}`);
                    }
                    setIsLoading(false);
                }
            });

        } catch (error) {
            setMessage(`Checkout Error: ${error.message}`);
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-4xl border-t-4 border-brand-primary">
            <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-brand-primary mb-2">
                    {user?.subscriptionStatus === 'active' ? 'Renew Your Subscription' : 'Activate Your Health Card'}
                </h2>
                <p className="text-gray-500 max-w-lg mx-auto">
                    {user?.subscriptionStatus === 'active'
                        ? 'Select a plan below to extend your current protection. The new validity period will be seamlessly added to your existing expiration date.'
                        : 'Choose a plan to instantly activate your digital health identity and securely store your emergency medical records.'}
                </p>
            </div>

            {message && (
                <div className={`mb-6 p-4 rounded-lg font-medium text-center ${message.includes('Successful') ? 'bg-green-50 text-green-700' : 'bg-brand-primary/10 text-brand-primary'}`}>
                    {message}
                </div>
            )}

            <h2 className="text-3xl font-extrabold text-brand-secondary text-center mb-8">Choose Your Protection Plan</h2>

            <div className="flex flex-wrap justify-center gap-8 md:gap-12 relative animate-fade-in-up">
                {pricingPlans.length > 0 ? (
                    pricingPlans.map((plan, index) => {
                        const isPopular = index === 1;
                        return (
                            <div key={plan.id} className={`w-full max-w-sm relative rounded-2xl p-6 flex flex-col items-center transition-all ${isPopular ? 'border-2 border-brand-primary shadow-xl shadow-brand-primary/10 transform md:-translate-y-2 bg-white' : 'border-2 border-gray-200 hover:border-brand-primary/50 bg-white'}`}>
                                {isPopular && (
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-brand-accent text-white px-4 py-1 rounded-full text-xs font-bold tracking-widest uppercase shadow-md">
                                        Best Value
                                    </div>
                                )}
                                <h3 className={`text-xl font-bold mb-2 ${isPopular ? 'text-brand-secondary' : 'text-gray-700'}`}>{plan.categoryName}</h3>
                                <div className="mb-1 text-center">
                                    {plan.mrp > plan.salePrice && <span className="text-xl font-bold line-through text-slate-300 mr-2">₹{parseInt(plan.mrp)}</span>}
                                    <span className="text-4xl font-black text-brand-primary">₹{parseInt(plan.salePrice)}</span>
                                </div>
                                <p className="text-sm font-medium text-gray-500 mb-4 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">Validity: {plan.validityMonths} months</p>

                                <ul className="text-sm text-gray-600 space-y-3 mb-8 w-full text-left">
                                    <li className="flex items-center"><svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Active Profile Status</li>
                                    <li className="flex items-center"><svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Sharable QR Digital Card</li>
                                    <li className="flex items-center"><svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> Edit Profiles Instantly</li>
                                </ul>
                                <button
                                    className={`w-full mt-auto py-3 px-4 font-bold rounded-full transition-all disabled:opacity-50 ${isPopular ? 'bg-brand-primary hover:bg-emerald-700 text-white shadow-lg transform hover:-translate-y-1' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
                                    onClick={() => handlePayment(plan)}
                                    disabled={isLoading || !isPaymentGatewayLoaded}
                                >
                                    {isLoading ? 'Processing...' : `${user?.subscriptionStatus === 'active' ? 'Renew with' : 'Select'} ${plan.categoryName}`}
                                </button>
                            </div>
                        )
                    })
                ) : (
                    <div className="w-full text-center py-10 text-slate-500">Loading pricing categories...</div>
                )}
            </div>
            <div className="mt-8 text-center border-t border-gray-100 pt-6">
                <p className="text-xs text-gray-400 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path></svg>
                    Secured by Cashfree Payments. Your details are fully encrypted.
                </p>
            </div>
        </div>
    );
}

export default CheckoutPage;
